import { useMutation, useQueryClient } from '@tanstack/react-query';
import api, { DocumentSignAssuranceTier, DocumentSignEnvelopeResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { addBreadcrumb, traceEnd, traceError, traceStart } from '@/lib/telemetry';

export interface CreateEnvelopeInput {
  /** Vault holder (`vh_…`) the envelope is created on behalf of. Required. */
  holderId: string;
  title: string;
  message?: string;
  signingOrder?: 'parallel' | 'sequential';
}

export function useCreateEnvelope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateEnvelopeInput) => {
      const traceId = traceStart('document-sign:envelope-create');
      try {
        const result = await api.createDocumentSignEnvelope(input);
        traceEnd(traceId, { envelopeId: result.envelope.envelopeId });
        addBreadcrumb('document-sign', 'Envelope created');
        return result.envelope;
      } catch (err) {
        traceError(traceId, err instanceof Error ? err : String(err));
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

/**
 * Set a new access code on a recipient of a SENT envelope. The recovery path
 * for a locked-out signer; the envelope detail refetches so the recipient's
 * attempt counter and verified state update in place.
 */
export function useResetRecipientAccessCode(envelopeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { recipientId: string; accessCode: string }) =>
      api.resetDocumentSignRecipientAccessCode(envelopeId, input.recipientId, input.accessCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      qc.invalidateQueries({ queryKey: queryKeys.documentSign.events(envelopeId) });
    },
  });
}

export function useUpdateEnvelope(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patch: {
      title?: string;
      message?: string | null;
      signingOrder?: 'parallel' | 'sequential';
      assuranceTier?: DocumentSignAssuranceTier;
    }): Promise<DocumentSignEnvelopeResponse> => {
      const result = await api.updateDocumentSignEnvelope(envelopeId, patch);
      return result.envelope;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

export function useDeleteEnvelope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (envelopeId: string) => {
      await api.deleteDocumentSignEnvelope(envelopeId);
      return envelopeId;
    },
    onSuccess: (envelopeId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

export function useSendEnvelope(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const traceId = traceStart('document-sign:envelope-send', { envelopeId });
      try {
        const result = await api.sendDocumentSignEnvelope(envelopeId);
        traceEnd(traceId, { notified: result.notifiedRecipientIds.length });
        addBreadcrumb('document-sign', 'Envelope sent for signature', { envelopeId });
        return result;
      } catch (err) {
        traceError(traceId, err instanceof Error ? err : String(err));
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

export function useVoidEnvelope(envelopeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reason: string) => api.voidDocumentSignEnvelope(envelopeId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

/**
 * Archive an envelope (soft-hide from the default list) and unarchive its
 * inverse. Both invalidate the same query branches as void / delete so
 * the list and detail views re-fetch and the row disappears (or
 * reappears) from the appropriate scope.
 */
export function useArchiveEnvelope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (envelopeId: string) => api.archiveDocumentSignEnvelope(envelopeId),
    onSuccess: (_data, envelopeId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}

export function useUnarchiveEnvelope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (envelopeId: string) => api.unarchiveDocumentSignEnvelope(envelopeId),
    onSuccess: (_data, envelopeId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelope(envelopeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentSign.envelopes() });
    },
  });
}
